import { ChangeEvent, FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AxiosError } from 'axios';
import styled from 'styled-components';

import { ModalContext } from 'src/hooks/useModal';
import { normalizeFamilyToken } from 'src/shared/utils';
import {
  fetchAdminStudents,
  sendAdminStudentReset,
  updateAdminStudent,
  type AdminStudentRecord,
  type UpdateAdminStudentPayload,
} from 'src/services/adminStudents';
import {
  createAdminMaterial,
  createAdminVideo,
  deleteAdminMaterial,
  deleteAdminVideo,
  listAdminMaterials,
  listAdminVideos,
  type AdminMaterialRecord,
  type AdminVideoRecord,
} from 'src/services/adminResources';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 28px 24px 40px;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  h1 {
    margin: 0;
    font-size: 2rem;
  }

  p {
    margin: 6px 0 0;
    color: #4a5568;
    max-width: 520px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
`;

const SummaryCard = styled.article`
  background: linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%);
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 10px 24px rgba(34, 43, 130, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;

  span {
    font-size: 0.85rem;
    color: #4a5568;
  }

  strong {
    font-size: 1.8rem;
    color: #1a237e;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ActionButton = styled.button`
  border-radius: 999px;
  padding: 10px 20px;
  border: 1px solid #222b82;
  background: transparent;
  color: #222b82;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus {
    outline: none;
    background: #222b82;
    color: #ffffff;
  }
`;

const Submenu = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  background: #ffffff;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
`;

const SubmenuButton = styled.button`
  border: 1px solid #d6d6d6;
  background: #ffffff;
  color: #1a202c;
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &[data-active='true'] {
    background: #222b82;
    border-color: #222b82;
    color: #ffffff;
    box-shadow: 0 6px 14px rgba(34, 43, 130, 0.18);
  }

  &:hover,
  &:focus {
    outline: none;
    border-color: #222b82;
  }
`;

const Section = styled.section`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 1.4rem;
  }

  p {
    margin: 4px 0 0;
    max-width: 420px;
    color: #4a5568;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SearchForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6d6;
  width: 280px;

  &:focus {
    outline: none;
    border-color: #222b82;
    box-shadow: 0 0 0 2px rgba(34, 43, 130, 0.15);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }

  thead {
    background: #f7f7f7;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #eef2ff;
  color: #222b82;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TableButton = styled.button`
  border: none;
  background: transparent;
  color: #222b82;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const DangerTableButton = styled(TableButton)`
  color: #d81e5b;

  &:hover,
  &:focus {
    color: #a31642;
  }
`;

const TableActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const DownloadLink = styled.a`
  color: #222b82;
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;

const HelperText = styled.span`
  font-size: 0.85rem;
  color: #4a5568;
  font-weight: 400;
`;

const AnswerList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style-type: none;

  li {
    position: relative;
    font-size: 0.85rem;
    color: #2d3748;
    padding-left: 16px;
  }

  li::before {
    content: attr(data-prefix);
    position: absolute;
    left: 0;
    font-weight: 600;
    color: #4a5568;
  }

  li[data-correct='true'] {
    font-weight: 600;
    color: #1a237e;
  }
`;

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const AnswerRadio = styled.input`
  margin: 0;
`;

const InlineButton = styled.button`
  border: none;
  background: transparent;
  color: #222b82;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  align-self: flex-start;

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;

const RemoveAnswerButton = styled(InlineButton)`
  color: #d81e5b;

  &:hover,
  &:focus {
    color: #a31642;
  }
`;

const CsvImporter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  background: #f5f7fb;

  strong {
    color: #1a237e;
  }
`;

const CsvActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  width: min(520px, 92vw);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.4rem;
  color: #1a237e;
`;

const ModalDescription = styled.p`
  margin: -4px 0 4px;
  color: #4a5568;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
  color: #222b82;
`;

const TextInput = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6d6;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #222b82;
    box-shadow: 0 0 0 2px rgba(34, 43, 130, 0.15);
  }

  &:disabled {
    background: #f8f9ff;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const AnswerTextInput = styled(TextInput)`
  flex: 1;
`;

const FileInput = styled.input`
  font-size: 0.95rem;

  &::file-selector-button {
    margin-right: 12px;
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid #222b82;
    background: #222b82;
    color: #fff;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  &::file-selector-button:hover {
    background: #1a237e;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6d6;
  min-height: 120px;
  resize: vertical;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #222b82;
    box-shadow: 0 0 0 2px rgba(34, 43, 130, 0.15);
  }

  &:disabled {
    background: #f8f9ff;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6d6;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #222b82;
    box-shadow: 0 0 0 2px rgba(34, 43, 130, 0.15);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CheckboxOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #d6d6d6;
  cursor: pointer;
  font-size: 0.9rem;
  color: #1a202c;

  input {
    accent-color: #222b82;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

const ResetPasswordButton = styled.button`
  margin-right: auto;
  border-radius: 999px;
  padding: 10px 18px;
  border: 1px solid #d81e5b;
  background: #ffffff;
  color: #d81e5b;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    background: #fff5f7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border-radius: 999px;
  padding: 10px 18px;
  border: 1px solid #cbd5f5;
  background: #ffffff;
  color: #1a237e;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    background: #edf2ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f3f4ff;
  }
`;

const PrimaryButton = styled.button`
  border-radius: 999px;
  padding: 10px 20px;
  border: none;
  background: linear-gradient(135deg, #222b82 0%, #3949ab 100%);
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    filter: none;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: #e53e3e;
  font-size: 0.85rem;
`;

const SuccessText = styled.p`
  margin: 0;
  color: #2f855a;
  font-size: 0.85rem;
`;

const TableSummary = styled.p`
  margin: 6px 0 0;
  color: #4a5568;
  font-size: 0.85rem;
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0 0;
`;

const LoadMoreButton = styled.button`
  border-radius: 999px;
  padding: 10px 24px;
  border: 1px solid #222b82;
  background: #ffffff;
  color: #222b82;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus {
    outline: none;
    background: #222b82;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f6f7ff;
    color: #9aa0d8;
    border-color: #cbd5f5;
  }
`;

type AdminMaterial = {
  id: string;
  title: string;
  fileName: string;
  fileUrl: string;
  families: string[];
  updatedAtLabel: string;
  updatedAtRaw: string | null;
};

type AdminVideo = {
  id: string;
  title: string;
  embedHtml: string;
  description: string;
  families: string[];
  updatedAtLabel: string;
  updatedAtRaw: string | null;
};

type RoleValue = 'student' | 'admin';

const ROLE_OPTIONS: ReadonlyArray<{ value: RoleValue; label: string }> = [
  { value: 'student', label: 'Aluno' },
  { value: 'admin', label: 'Administrador' },
] as const;

const ROLE_LABELS: Record<RoleValue, string> = {
  student: 'Aluno',
  admin: 'Administrador',
} as const;

type AdminStudent = {
  id: number;
  name: string;
  email: string;
  role: RoleValue;
  lastAccessAt: string | null;
  createdAt: string;
};

type FamilyOption = {
  slug: string;
  label: string;
};

const ADMIN_SECTIONS = [
  { id: 'materiais', label: 'Materiais' },
  { id: 'videos', label: 'Aulas/Vídeos' },
  { id: 'alunos', label: 'Alunos' },
] as const;

type AdminSection = (typeof ADMIN_SECTIONS)[number]['id'];

const DEFAULT_SECTION: AdminSection = 'materiais';

function normalizeSectionId(candidate: string | null | undefined): AdminSection {
  if (!candidate) {
    return DEFAULT_SECTION;
  }

  const normalized = candidate.replace(/^#/, '').trim().toLowerCase();
  const match = ADMIN_SECTIONS.find(section => section.id === normalized);
  return match ? match.id : DEFAULT_SECTION;
}

function readSectionFromHash(): AdminSection {
  if (typeof window === 'undefined') {
    return DEFAULT_SECTION;
  }

  return normalizeSectionId(window.location.hash);
}

type MaterialFormData = {
  title: string;
  file: File;
  families: string[];
};

type VideoFormData = {
  title: string;
  embedHtml: string;
  description: string;
  families: string[];
};

const MATERIALS_PAGE_SIZE = 25;
const VIDEOS_PAGE_SIZE = 25;
const STUDENTS_PAGE_SIZE = 25;

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
});

function formatDateLabel(value?: string | null) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return dateFormatter.format(date);
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return dateTimeFormatter.format(date);
}

function normalizeStudentRole(raw: unknown): RoleValue {
  if (raw === undefined || raw === null) {
    return 'student';
  }

  const normalized = String(raw).trim().toLowerCase();

  if (normalized === 'admin' || normalized === 'administrator' || normalized === '2') {
    return 'admin';
  }

  return 'student';
}

function mapStudentRecord(record: AdminStudentRecord): AdminStudent {
  const rawRole = record.role_slug ?? record.role?.slug ?? record.role_name ?? record.role_id;

  return {
    id: record.id,
    name: record.name,
    email: record.email,
    role: normalizeStudentRole(rawRole),
    lastAccessAt: record.last_login_at ?? null,
    createdAt: record.created_at,
  };
}

function mapMaterialRecord(record: AdminMaterialRecord): AdminMaterial {
  const rawDate = record.updated_at ?? record.published_at ?? record.created_at;

  return {
    id: String(record.id),
    title: record.title,
    fileName: record.file_name,
    fileUrl: record.file_url ?? '',
    families: Array.isArray(record.families)
      ? record.families.filter((item): item is string => typeof item === 'string')
      : [],
    updatedAtLabel: formatDateLabel(rawDate),
    updatedAtRaw: rawDate ?? null,
  };
}

function mapVideoRecord(record: AdminVideoRecord): AdminVideo {
  const rawDate = record.updated_at ?? record.published_at ?? record.created_at;

  return {
    id: String(record.id),
    title: record.title,
    embedHtml: record.embed_html ?? '',
    description: record.description ?? '',
    families: Array.isArray(record.families)
      ? record.families.filter((item): item is string => typeof item === 'string')
      : [],
    updatedAtLabel: formatDateLabel(rawDate),
    updatedAtRaw: rawDate ?? null,
  };
}

type ApiErrorPayload = {
  message?: string;
  status?: string;
  errors?: Record<string, unknown>;
};

function isApiError(error: unknown): error is AxiosError<ApiErrorPayload> {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (isApiError(error)) {
    const data = error.response?.data;

    if (data?.errors && typeof data.errors === 'object') {
      for (const value of Object.values(data.errors)) {
        if (Array.isArray(value)) {
          const first = value.find(item => typeof item === 'string');
          if (typeof first === 'string' && first.trim()) {
            return first;
          }
        } else if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }

    if (typeof data?.status === 'string' && data.status.trim()) {
      return data.status;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function previewHtml(html: string, maxLength = 80) {
  const normalized = html.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1)}…`;
}

type NormalizeVideoEmbedResult =
  | { success: true; embedHtml: string }
  | { success: false; error: string };

function parseYouTubeStartParam(raw: string | null) {
  if (!raw) {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    return Number.parseInt(trimmed, 10);
  }

  const match = trimmed.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!match) {
    return null;
  }

  const [, hours, minutes, seconds] = match;
  const total =
    (hours ? Number.parseInt(hours, 10) * 3600 : 0) +
    (minutes ? Number.parseInt(minutes, 10) * 60 : 0) +
    (seconds ? Number.parseInt(seconds, 10) : 0);

  return Number.isNaN(total) || total <= 0 ? null : total;
}

function extractYouTubeVideoId(url: URL) {
  const pathname = url.pathname.replace(/\/+$/, '');
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    const id = url.searchParams.get('v');
    return id && id.trim() ? id.trim() : null;
  }

  if (segments[0] === 'watch') {
    const id = url.searchParams.get('v');
    return id && id.trim() ? id.trim() : null;
  }

  if (segments[0] === 'embed' && segments[1]) {
    return segments[1];
  }

  if (segments[0] === 'shorts' && segments[1]) {
    return segments[1];
  }

  return segments[segments.length - 1];
}

function extractVimeoVideoId(url: URL) {
  const segments = url.pathname.split('/').filter(Boolean).reverse();
  return segments.find(segment => /^\d+$/.test(segment)) ?? null;
}

function buildIframeHtml(src: string, providerTitle: string, allow: string) {
  const normalizedSrc = src.trim();
  return `<iframe src="${normalizedSrc}" title="${providerTitle}" allow="${allow}" allowfullscreen style="border:0;width:100%;aspect-ratio:16/9;min-height:320px;"></iframe>`;
}

function normalizeVideoEmbedInput(rawInput: string): NormalizeVideoEmbedResult {
  const trimmed = rawInput.trim();

  if (!trimmed) {
    return {
      success: false,
      error: 'Informe título, código embed ou link e pelo menos uma família.',
    };
  }

  if (/^<iframe[\s\S]*><\/iframe>$/i.test(trimmed)) {
    return { success: true, embedHtml: trimmed };
  }

  let url: URL;

  try {
    url = new URL(trimmed);
  } catch (error) {
    return {
      success: false,
      error: 'Informe um código embed do Vimeo ou um link válido do YouTube.',
    };
  }

  const hostname = url.hostname.replace(/^www\./, '').toLowerCase();

  if (hostname === 'youtu.be' || hostname.endsWith('youtube.com')) {
    const videoId = extractYouTubeVideoId(url);

    if (!videoId) {
      return {
        success: false,
        error: 'Não foi possível identificar o vídeo do YouTube informado.',
      };
    }

    const startParam = parseYouTubeStartParam(url.searchParams.get('start') ?? url.searchParams.get('t'));
    const params = new URLSearchParams();
    if (startParam != null) {
      params.set('start', startParam.toString());
    }

    const embedUrl = `https://www.youtube.com/embed/${videoId}${params.toString() ? `?${params.toString()}` : ''}`;

    return {
      success: true,
      embedHtml: buildIframeHtml(
        embedUrl,
        'Player do YouTube',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      ),
    };
  }

  if (hostname === 'vimeo.com' || hostname.endsWith('.vimeo.com')) {
    const videoId = extractVimeoVideoId(url);

    if (!videoId) {
      return {
        success: false,
        error: 'Não foi possível identificar o vídeo do Vimeo informado.',
      };
    }

    const embedUrl = hostname === 'player.vimeo.com' ? url.toString() : `https://player.vimeo.com/video/${videoId}`;

    return {
      success: true,
      embedHtml: buildIframeHtml(embedUrl, 'Player do Vimeo', 'autoplay; fullscreen; picture-in-picture'),
    };
  }

  return {
    success: true,
    embedHtml: trimmed,
  };
}

export function Admin() {
  const { setOpen, setModalChildren } = useContext(ModalContext);

  const handlePlaceholderAction = useCallback(() => undefined, []);

  useEffect(() => {
    document.title = 'Administração | ART - Simulatoro';
  }, []);

  const [activeSection, setActiveSection] = useState<AdminSection>(() => readSectionFromHash());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleHashChange = () => {
      const nextSection = readSectionFromHash();
      setActiveSection(prev => (prev === nextSection ? prev : nextSection));
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const targetHash = `#${activeSection}`;
    if (window.location.hash !== targetHash) {
      window.history.replaceState(null, '', targetHash);
    }
  }, [activeSection]);
  const [studentsTotal, setStudentsTotal] = useState<number | null>(null);
  const [materials, setMaterials] = useState<AdminMaterial[]>([]);
  const [videos, setVideos] = useState<AdminVideo[]>([]);

  const [students, setStudents] = useState<AdminStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState('');
  const [studentsFeedback, setStudentsFeedback] = useState('');
  const [studentsQuery, setStudentsQuery] = useState('');
  const [studentsSearch, setStudentsSearch] = useState('');
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsLastPage, setStudentsLastPage] = useState(1);
  const [studentsLoadingMore, setStudentsLoadingMore] = useState(false);

  const [materialsTotal, setMaterialsTotal] = useState<number | null>(null);
  const [videosTotal, setVideosTotal] = useState<number | null>(null);

  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState('');
  const [materialsFeedback, setMaterialsFeedback] = useState('');
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState('');
  const [videosFeedback, setVideosFeedback] = useState('');

  const [materialsFilter, setMaterialsFilter] = useState('');
  const [materialsFamilyFilter, setMaterialsFamilyFilter] = useState('');
  const [videosFilter, setVideosFilter] = useState('');
  const [videosFamilyFilter, setVideosFamilyFilter] = useState('');

  const [materialsPage, setMaterialsPage] = useState(1);
  const [materialsLastPage, setMaterialsLastPage] = useState(1);
  const [materialsLoadingMore, setMaterialsLoadingMore] = useState(false);

  const [videosPage, setVideosPage] = useState(1);
  const [videosLastPage, setVideosLastPage] = useState(1);
  const [videosLoadingMore, setVideosLoadingMore] = useState(false);

  const loadMaterials = useCallback(
    async (page = 1, append = false) => {
      if (append) {
        setMaterialsLoadingMore(true);
      } else {
        setMaterialsLoading(true);
        setMaterialsPage(1);
        setMaterialsLastPage(1);
      }

      setMaterialsError('');

      try {
        const response = await listAdminMaterials({
          page,
          perPage: MATERIALS_PAGE_SIZE,
        });
        const mapped = response.data.map(mapMaterialRecord);

        setMaterials(prev => {
          const registry = new Map<string, AdminMaterial>();
          if (append) {
            prev.forEach(item => registry.set(item.id, item));
          }
          mapped.forEach(item => registry.set(item.id, item));
          const result = Array.from(registry.values());
          result.sort((a, b) => {
            const aDate = a.updatedAtRaw ?? '';
            const bDate = b.updatedAtRaw ?? '';
            return bDate.localeCompare(aDate);
          });
          return result;
        });

        setMaterialsTotal(response.meta.total ?? mapped.length);
        setMaterialsPage(response.meta.current_page ?? page);
        setMaterialsLastPage(response.meta.last_page ?? page);
      } catch (error) {
        if (!append) {
          setMaterials([]);
          setMaterialsTotal(null);
        }
        setMaterialsError(
          extractErrorMessage(error, 'Não foi possível carregar os materiais no momento.'),
        );
      } finally {
        if (append) {
          setMaterialsLoadingMore(false);
        } else {
          setMaterialsLoading(false);
        }
      }
    },
    [],
  );

  const loadVideos = useCallback(
    async (page = 1, append = false) => {
      if (append) {
        setVideosLoadingMore(true);
      } else {
        setVideosLoading(true);
        setVideosPage(1);
        setVideosLastPage(1);
      }

      setVideosError('');

      try {
        const response = await listAdminVideos({
          page,
          perPage: VIDEOS_PAGE_SIZE,
        });
        const mapped = response.data.map(mapVideoRecord);

        setVideos(prev => {
          const registry = new Map<string, AdminVideo>();
          if (append) {
            prev.forEach(item => registry.set(item.id, item));
          }
          mapped.forEach(item => registry.set(item.id, item));
          const result = Array.from(registry.values());
          result.sort((a, b) => {
            const aDate = a.updatedAtRaw ?? '';
            const bDate = b.updatedAtRaw ?? '';
            return bDate.localeCompare(aDate);
          });
          return result;
        });

        setVideosTotal(response.meta.total ?? mapped.length);
        setVideosPage(response.meta.current_page ?? page);
        setVideosLastPage(response.meta.last_page ?? page);
      } catch (error) {
        if (!append) {
          setVideos([]);
          setVideosTotal(null);
        }
        setVideosError(
          extractErrorMessage(error, 'Não foi possível carregar os vídeos no momento.'),
        );
      } finally {
        if (append) {
          setVideosLoadingMore(false);
        } else {
          setVideosLoading(false);
        }
      }
    },
    [],
  );


  const materialsCount = materialsTotal ?? materials.length;
  const videosCount = videosTotal ?? videos.length;

  const overviewCards = useMemo(
    () => [
      { label: 'Materiais publicados', value: materialsCount.toString() },
      { label: 'Vídeo-aulas ativas', value: videosCount.toString() },
      { label: 'Alunos ativos', value: studentsTotal != null ? studentsTotal.toString() : '–' },
    ],
    [materialsCount, videosCount, studentsTotal],
  );

  const { categories } = useCourseCategories();
  const familyOptions = useMemo(() => {
    const seen = new Set<string>();
    return categories
      .map(category => {
        const slug = category.slug ?? normalizeFamilyToken(category.name);
        const label = category.fullName ?? category.name ?? slug;
        const token = normalizeFamilyToken(slug);
        if (!slug || !label || !token || seen.has(token)) {
          return null;
        }
        seen.add(token);
        return { slug, label } satisfies FamilyOption;
      })
      .filter((option): option is FamilyOption => Boolean(option));
  }, [categories]);

  const familyLabelMap = useMemo(
    () => new Map(familyOptions.map(item => [item.slug, item.label])),
    [familyOptions],
  );

  const closeModal = useCallback(() => {
    setOpen(false);
    setModalChildren(null);
  }, [setModalChildren, setOpen]);

  const loadStudents = useCallback(
    async (search: string, page = 1, append = false) => {
      if (append) {
        setStudentsLoadingMore(true);
      } else {
        setStudentsLoading(true);
        setStudentsPage(1);
        setStudentsLastPage(1);
      }

      setStudentsError('');
      let success = false;

      try {
        const response = await fetchAdminStudents({
          search: search || undefined,
          page,
          perPage: STUDENTS_PAGE_SIZE,
        });
        const mapped = response.data.map(mapStudentRecord);

        setStudents(prev => {
          if (!append) {
            return mapped;
          }

          const registry = new Map<number, AdminStudent>();
          prev.forEach(item => registry.set(item.id, item));
          mapped.forEach(item => registry.set(item.id, item));
          return Array.from(registry.values());
        });

        const total =
          typeof response.meta?.total === 'number' ? response.meta.total : mapped.length;
        setStudentsTotal(total);
        setStudentsPage(response.meta?.current_page ?? page);
        setStudentsLastPage(response.meta?.last_page ?? page);
        success = true;
      } catch (error) {
        if (!append) {
          setStudents([]);
          setStudentsTotal(null);
        }
        setStudentsError(
          extractErrorMessage(error, 'Não foi possível carregar os alunos no momento.'),
        );
      } finally {
        if (append) {
          setStudentsLoadingMore(false);
        } else {
          setStudentsLoading(false);
        }
      }

      return success;
    },
    [],
  );

  useEffect(() => {
    loadStudents(studentsSearch);
  }, [loadStudents, studentsSearch]);

  useEffect(() => {
    loadMaterials();
    loadVideos();
  }, [loadMaterials, loadVideos]);

  const handleStudentsSearch = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const normalized = studentsQuery.trim();
      setStudentsFeedback('');
      setStudentsError('');
      setStudentsSearch(normalized);
    },
    [studentsQuery],
  );

  const handleStudentsClear = useCallback(() => {
    setStudentsQuery('');
    setStudentsFeedback('');
    setStudentsError('');
    setStudentsSearch('');
  }, []);

  const handleLoadMoreMaterials = useCallback(() => {
    loadMaterials(materialsPage + 1, true);
  }, [loadMaterials, materialsPage]);

  const handleLoadMoreVideos = useCallback(() => {
    loadVideos(videosPage + 1, true);
  }, [loadVideos, videosPage]);

  const handleLoadMoreStudents = useCallback(() => {
    loadStudents(studentsSearch, studentsPage + 1, true);
  }, [loadStudents, studentsPage, studentsSearch]);

  const handleStudentUpdate = useCallback(
    async (studentId: number, values: UpdateAdminStudentPayload) => {
      try {
        await updateAdminStudent(studentId, values);
        const success = await loadStudents(studentsSearch);
        if (success) {
          setStudentsError('');
        }
        const feedbackMessage =
          values.role === 'admin'
            ? 'Perfil atualizado. O usuário agora é administrador e não aparecerá mais na lista de alunos.'
            : 'Perfil do aluno atualizado com sucesso.';
        setStudentsFeedback(feedbackMessage);
        closeModal();
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Não foi possível atualizar o aluno.'));
      }
    },
    [closeModal, loadStudents, studentsSearch],
  );

  const handleStudentReset = useCallback(
    async (studentId: number) => {
      try {
        const response = await sendAdminStudentReset(studentId);
        const message =
          (typeof response?.status === 'string' && response.status.trim()) ||
          'Enviamos um email com instruções para redefinir a senha.';
        setStudentsFeedback(message);
        setStudentsError('');
        return message;
      } catch (error) {
        throw new Error(
          extractErrorMessage(error, 'Não foi possível enviar o email de redefinição de senha.'),
        );
      }
    },
    [],
  );

  const openStudentModal = useCallback(
    (student: AdminStudent) => {
      setModalChildren(
        <StudentModal
          student={student}
          onSubmit={values => handleStudentUpdate(student.id, values)}
          onSendReset={() => handleStudentReset(student.id)}
          onCancel={closeModal}
        />,
      );
      setOpen(true);
    },
    [closeModal, handleStudentReset, handleStudentUpdate, setModalChildren, setOpen],
  );

  const handleCreateMaterial = useCallback(
    async (data: MaterialFormData) => {
      setMaterialsError('');
      setMaterialsFeedback('');

      const uniqueFamilies = Array.from(new Set(data.families));

      try {
        const record = await createAdminMaterial({
          title: data.title,
          families: uniqueFamilies,
          file: data.file,
        });

        const mapped = mapMaterialRecord(record);
        setMaterials(prev => {
          const next = [mapped, ...prev.filter(item => item.id !== mapped.id)];
          next.sort((a, b) => {
            const aDate = a.updatedAtRaw ?? '';
            const bDate = b.updatedAtRaw ?? '';
            return bDate.localeCompare(aDate);
          });
          return next;
        });
        setMaterialsTotal(prev => (prev !== null ? prev + 1 : null));

        setMaterialsFeedback('Material salvo com sucesso.');
        closeModal();
      } catch (error) {
        throw new Error(
          extractErrorMessage(error, 'Não foi possível salvar o material no momento.'),
        );
      }
    },
    [closeModal],
  );

  const handleCreateVideo = useCallback(
    async (data: VideoFormData) => {
      setVideosError('');
      setVideosFeedback('');

      const uniqueFamilies = Array.from(new Set(data.families));

      try {
        const record = await createAdminVideo({
          title: data.title,
          description: data.description,
          embedHtml: data.embedHtml,
          families: uniqueFamilies,
        });

        const mapped = mapVideoRecord(record);
        setVideos(prev => {
          const next = [mapped, ...prev.filter(item => item.id !== mapped.id)];
          next.sort((a, b) => {
            const aDate = a.updatedAtRaw ?? '';
            const bDate = b.updatedAtRaw ?? '';
            return bDate.localeCompare(aDate);
          });
          return next;
        });
        setVideosTotal(prev => (prev !== null ? prev + 1 : null));

        setVideosFeedback('Vídeo cadastrado com sucesso.');
        closeModal();
      } catch (error) {
        throw new Error(
          extractErrorMessage(error, 'Não foi possível salvar o vídeo no momento.'),
        );
      }
    },
    [closeModal],
  );

  const handleDeleteMaterial = useCallback(async (materialId: string) => {
    setMaterialsError('');
    setMaterialsFeedback('');

    const numericId = Number.parseInt(materialId, 10);
    const identifier = Number.isNaN(numericId) ? materialId : numericId;

    try {
      await deleteAdminMaterial(identifier);
      setMaterials(prev => prev.filter(item => item.id !== materialId));
      setMaterialsTotal(prev => (prev !== null ? Math.max(prev - 1, 0) : null));
      setMaterialsFeedback('Material removido com sucesso.');
    } catch (error) {
      setMaterialsError(
        extractErrorMessage(error, 'Não foi possível remover o material no momento.'),
      );
    }
  }, []);

  const handleDeleteVideo = useCallback(async (videoId: string) => {
    setVideosError('');
    setVideosFeedback('');

    const numericId = Number.parseInt(videoId, 10);
    const identifier = Number.isNaN(numericId) ? videoId : numericId;

    try {
      await deleteAdminVideo(identifier);
      setVideos(prev => prev.filter(item => item.id !== videoId));
      setVideosTotal(prev => (prev !== null ? Math.max(prev - 1, 0) : null));
      setVideosFeedback('Vídeo removido com sucesso.');
    } catch (error) {
      setVideosError(
        extractErrorMessage(error, 'Não foi possível remover o vídeo no momento.'),
      );
    }
  }, []);

  const openMaterialModal = useCallback(() => {
    setModalChildren(
      <MaterialModal
        familyOptions={familyOptions}
        onSubmit={handleCreateMaterial}
        onCancel={closeModal}
      />,
    );
    setOpen(true);
  }, [closeModal, familyOptions, handleCreateMaterial, setModalChildren, setOpen]);

  const openVideoModal = useCallback(() => {
    setModalChildren(
      <VideoModal
        familyOptions={familyOptions}
        onSubmit={handleCreateVideo}
        onCancel={closeModal}
      />,
    );
    setOpen(true);
  }, [closeModal, familyOptions, handleCreateVideo, setModalChildren, setOpen]);

  const filteredMaterials = useMemo(() => {
    const query = materialsFilter.trim().toLowerCase();
    const familySlug = materialsFamilyFilter;

    return materials.filter(item => {
      const matchesFamily = !familySlug || item.families.includes(familySlug);
      if (!matchesFamily) {
        return false;
      }

      if (!query) {
        return true;
      }

      const matchesFamilies = item.families.some(slug =>
        (familyLabelMap.get(slug) ?? slug).toLowerCase().includes(query),
      );

      return (
        item.title.toLowerCase().includes(query) ||
        item.fileName.toLowerCase().includes(query) ||
        item.updatedAtLabel.toLowerCase().includes(query) ||
        matchesFamilies
      );
    });
  }, [familyLabelMap, materials, materialsFamilyFilter, materialsFilter]);

  const filteredVideos = useMemo(() => {
    const query = videosFilter.trim().toLowerCase();
    const familySlug = videosFamilyFilter;

    return videos.filter(item => {
      const matchesFamily = !familySlug || item.families.includes(familySlug);
      if (!matchesFamily) {
        return false;
      }

      if (!query) {
        return true;
      }

      const matchesFamilies = item.families.some(slug =>
        (familyLabelMap.get(slug) ?? slug).toLowerCase().includes(query),
      );

      return (
        item.title.toLowerCase().includes(query) ||
        item.embedHtml.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.updatedAtLabel.toLowerCase().includes(query) ||
        matchesFamilies
      );
    });
  }, [familyLabelMap, videos, videosFamilyFilter, videosFilter]);

  const materialsHasFilters =
    materialsFilter.trim().length > 0 || materialsFamilyFilter.trim().length > 0;
  const totalMaterialsAvailable = materialsTotal ?? materials.length;
  let materialsSummaryText = 'Nenhum material cadastrado.';
  if (materialsHasFilters) {
    materialsSummaryText =
      filteredMaterials.length === 0
        ? 'Nenhum material encontrado para os filtros selecionados.'
        : `Exibindo ${filteredMaterials.length} material(is) dentro dos filtros aplicados.`;
  } else if (totalMaterialsAvailable > 0) {
    const displayed = filteredMaterials.length;
    materialsSummaryText = `Exibindo ${displayed} de ${totalMaterialsAvailable} materiais`;
    if (totalMaterialsAvailable > displayed) {
      materialsSummaryText += ' (use "Carregar mais" para ver todos).';
    }
  }

  const videosHasFilters =
    videosFilter.trim().length > 0 || videosFamilyFilter.trim().length > 0;
  const totalVideosAvailable = videosTotal ?? videos.length;
  let videosSummaryText = 'Nenhum vídeo cadastrado.';
  if (videosHasFilters) {
    videosSummaryText =
      filteredVideos.length === 0
        ? 'Nenhum vídeo encontrado para os filtros selecionados.'
        : `Exibindo ${filteredVideos.length} vídeo(s) dentro dos filtros aplicados.`;
  } else if (totalVideosAvailable > 0) {
    const displayed = filteredVideos.length;
    videosSummaryText = `Exibindo ${displayed} de ${totalVideosAvailable} vídeos`;
    if (totalVideosAvailable > displayed) {
      videosSummaryText += ' (use "Carregar mais" para ver todos).';
    }
  }

  const filteredStudents = students;

  const studentsHasFilters = studentsSearch.trim().length > 0;
  const totalDisplayedStudents = filteredStudents.length;
  const totalAvailableStudents = studentsTotal ?? students.length;
  let studentsSummaryText = 'Nenhum aluno encontrado.';
  if (studentsHasFilters) {
    studentsSummaryText =
      totalDisplayedStudents === 0
        ? 'Nenhum aluno corresponde ao filtro informado.'
        : `Exibindo ${totalDisplayedStudents} aluno(s) filtrados de ${totalAvailableStudents} cadastrados.`;
  } else if (totalAvailableStudents > 0) {
    studentsSummaryText = `Exibindo ${totalDisplayedStudents} de ${totalAvailableStudents} alunos`;
    if (totalAvailableStudents > totalDisplayedStudents) {
      studentsSummaryText += ` (mostrando os primeiros ${totalDisplayedStudents} resultados)`;
    }
  }

  const materialsSummaryDisplay =
    materialsLoading || materialsLoadingMore ? 'Carregando materiais...' : materialsSummaryText;
  const videosSummaryDisplay =
    videosLoading || videosLoadingMore ? 'Carregando vídeos...' : videosSummaryText;
  const studentsSummaryDisplay =
    studentsLoading || studentsLoadingMore ? 'Carregando alunos...' : studentsSummaryText;

  return (
    <Page>
      <Header>
        <div>
          <h1>Administração</h1>
          <p>Gerencie conteúdos, aulas e acompanhe o progresso dos alunos dentro do Simulatoro.</p>
        </div>
      </Header>

      <SummaryGrid>
        {overviewCards.map(card => (
          <SummaryCard key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <Submenu>
        {ADMIN_SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          return (
            <SubmenuButton
              key={section.id}
              type="button"
              data-active={isActive}
              aria-pressed={isActive}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </SubmenuButton>
          );
        })}
      </Submenu>

      {activeSection === 'materiais' ? (
        <Section>
          <SectionHeader>
            <div>
              <h2>Materiais</h2>
              <p>Envie arquivos (PDFs, planilhas, apresentações) e disponibilize para as famílias corretas.</p>
            </div>
          <Actions>
            <ActionButton type="button" onClick={openMaterialModal}>
              Novo material
            </ActionButton>
          </Actions>
        </SectionHeader>
        <Toolbar>
          <ButtonRow>
            <SearchInput
              placeholder="Buscar material"
              value={materialsFilter}
              onChange={event => setMaterialsFilter(event.target.value)}
            />
            <Select
              value={materialsFamilyFilter}
              onChange={event => setMaterialsFamilyFilter(event.target.value)}
            >
              <option value="">Todas as famílias</option>
              {familyOptions.map(option => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </Select>
            {materialsFamilyFilter ? (
              <InlineButton type="button" onClick={() => setMaterialsFamilyFilter('')}>
                Limpar família
              </InlineButton>
            ) : null}
          </ButtonRow>
        </Toolbar>
        {materialsError ? <ErrorText>{materialsError}</ErrorText> : null}
        {materialsFeedback ? <SuccessText>{materialsFeedback}</SuccessText> : null}
        {(!materialsError || materialsLoading || materialsLoadingMore) && (
          <TableSummary>{materialsSummaryDisplay}</TableSummary>
        )}
        <Table>
          <thead>
            <tr>
              <th>Título</th>
              <th>Arquivo</th>
              <th>Famílias</th>
              <th>Alteração</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {materialsLoading ? (
              <tr>
                <td colSpan={5}>Carregando materiais...</td>
              </tr>
            ) : filteredMaterials.length === 0 ? (
              <tr>
                <td colSpan={5}>{materialsSummaryText}</td>
              </tr>
            ) : (
              filteredMaterials.map(item => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>
                    {item.fileUrl ? (
                      <DownloadLink href={item.fileUrl} target="_blank" rel="noopener noreferrer" download>
                        {item.fileName}
                      </DownloadLink>
                    ) : (
                      item.fileName
                    )}
                  </td>
                  <td>
                    <TagList>
                      {item.families.map(family => (
                        <Tag key={family}>{familyLabelMap.get(family) ?? family}</Tag>
                      ))}
                    </TagList>
                  </td>
                  <td>{item.updatedAtLabel}</td>
                  <td>
                    <TableActions>
                      <DangerTableButton type="button" onClick={() => handleDeleteMaterial(item.id)}>
                        Remover
                      </DangerTableButton>
                    </TableActions>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {materialsPage < materialsLastPage ? (
          <LoadMoreWrapper>
            <LoadMoreButton
              type="button"
              onClick={handleLoadMoreMaterials}
              disabled={materialsLoadingMore || materialsLoading}
            >
              {materialsLoadingMore ? 'Carregando...' : 'Carregar mais materiais'}
            </LoadMoreButton>
          </LoadMoreWrapper>
        ) : null}
        </Section>
      ) : null}

      {activeSection === 'videos' ? (
        <Section>
          <SectionHeader>
            <div>
              <h2>Aulas / vídeos</h2>
              <p>
                Cole o embed do Vimeo ou o link do YouTube, adicione descrições e mantenha as aulas
              atualizadas por família.
            </p>
          </div>
          <Actions>
            <ActionButton type="button" onClick={openVideoModal}>
              Novo vídeo
            </ActionButton>
          </Actions>
        </SectionHeader>
        <Toolbar>
          <ButtonRow>
            <SearchInput
              placeholder="Buscar vídeo"
              value={videosFilter}
              onChange={event => setVideosFilter(event.target.value)}
            />
            <Select
              value={videosFamilyFilter}
              onChange={event => setVideosFamilyFilter(event.target.value)}
            >
              <option value="">Todas as famílias</option>
              {familyOptions.map(option => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </Select>
            {videosFamilyFilter ? (
              <InlineButton type="button" onClick={() => setVideosFamilyFilter('')}>
                Limpar família
              </InlineButton>
            ) : null}
          </ButtonRow>
        </Toolbar>
        {videosError ? <ErrorText>{videosError}</ErrorText> : null}
        {videosFeedback ? <SuccessText>{videosFeedback}</SuccessText> : null}
        {(!videosError || videosLoading || videosLoadingMore) && (
          <TableSummary>{videosSummaryDisplay}</TableSummary>
        )}
        <Table>
          <thead>
            <tr>
              <th>Título</th>
              <th>Embed</th>
              <th>Famílias</th>
              <th>Alteração</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {videosLoading ? (
              <tr>
                <td colSpan={5}>Carregando vídeos...</td>
              </tr>
            ) : filteredVideos.length === 0 ? (
              <tr>
                <td colSpan={5}>{videosSummaryText}</td>
              </tr>
            ) : (
              filteredVideos.map(item => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>
                    <code>{previewHtml(item.embedHtml)}</code>
                  </td>
                  <td>
                    <TagList>
                      {item.families.map(family => (
                        <Tag key={family}>{familyLabelMap.get(family) ?? family}</Tag>
                      ))}
                    </TagList>
                  </td>
                  <td>{item.updatedAtLabel}</td>
                  <td>
                    <TableActions>
                      <DangerTableButton type="button" onClick={() => handleDeleteVideo(item.id)}>
                        Remover
                      </DangerTableButton>
                    </TableActions>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {videosPage < videosLastPage ? (
          <LoadMoreWrapper>
            <LoadMoreButton
              type="button"
              onClick={handleLoadMoreVideos}
              disabled={videosLoadingMore || videosLoading}
            >
              {videosLoadingMore ? 'Carregando...' : 'Carregar mais vídeos'}
            </LoadMoreButton>
          </LoadMoreWrapper>
        ) : null}
        </Section>
      ) : null}

      {activeSection === 'alunos' ? (
        <Section>
          <SectionHeader>
            <div>
              <h2>Alunos</h2>
              <p>Acompanhe quem está acessando os materiais e simulados.</p>
            </div>
        </SectionHeader>
        <Toolbar>
          <SearchForm onSubmit={handleStudentsSearch}>
            <SearchInput
              type="search"
              placeholder="Buscar no banco por email"
              value={studentsQuery}
              onChange={event => setStudentsQuery(event.target.value)}
            />
            <ActionButton type="submit">Buscar</ActionButton>
            {studentsSearch ? (
              <InlineButton type="button" onClick={handleStudentsClear}>
                Limpar busca
              </InlineButton>
            ) : null}
          </SearchForm>
        </Toolbar>
        {studentsFeedback ? <SuccessText>{studentsFeedback}</SuccessText> : null}
        {studentsError && !studentsLoading ? <ErrorText>{studentsError}</ErrorText> : null}
        {!studentsError || studentsLoading || studentsLoadingMore ? (
          <TableSummary>{studentsSummaryDisplay}</TableSummary>
        ) : null}
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Último acesso</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {studentsLoading ? (
              <tr>
                <td colSpan={5}>Carregando alunos...</td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  {studentsHasFilters
                    ? 'Nenhum aluno encontrado para o filtro informado.'
                    : studentsError || 'Nenhum aluno encontrado.'}
                </td>
              </tr>
            ) : (
              filteredStudents.map(item => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>{ROLE_LABELS[item.role]}</td>
                  <td>{formatDateTime(item.lastAccessAt ?? item.createdAt)}</td>
                  <td>
                    <TableButton type="button" onClick={() => openStudentModal(item)}>
                      Editar
                    </TableButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {studentsPage < studentsLastPage ? (
          <LoadMoreWrapper>
            <LoadMoreButton
              type="button"
              onClick={handleLoadMoreStudents}
              disabled={studentsLoadingMore || studentsLoading}
            >
              {studentsLoadingMore ? 'Carregando...' : 'Carregar mais alunos'}
            </LoadMoreButton>
          </LoadMoreWrapper>
        ) : null}
        </Section>
      ) : null}
    </Page>
  );
}

type StudentModalProps = {
  student: AdminStudent;
  onSubmit: (values: UpdateAdminStudentPayload) => Promise<void>;
  onSendReset: () => Promise<string>;
  onCancel: () => void;
};

function StudentModal({ student, onSubmit, onSendReset, onCancel }: StudentModalProps) {
  const [name, setName] = useState(student.name);
  const [email, setEmail] = useState(student.email);
  const [role, setRole] = useState<RoleValue>(student.role);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();

      if (!trimmedName || !trimmedEmail) {
        setError('Informe nome, email e perfil do aluno.');
        return;
      }

      setSaving(true);
      setError(null);
      setFeedback(null);

      try {
        await onSubmit({ name: trimmedName, email: trimmedEmail, role });
      } catch (submitError) {
        setError(
          submitError instanceof Error
            ? submitError.message
            : 'Não foi possível atualizar o aluno.',
        );
      } finally {
        setSaving(false);
      }
    },
    [email, name, onSubmit],
  );

  const handleReset = useCallback(async () => {
    setResetting(true);
    setError(null);

    try {
      const message = await onSendReset();
      setFeedback(message);
    } catch (resetError) {
      setFeedback(null);
      setError(
        resetError instanceof Error
          ? resetError.message
          : 'Não foi possível enviar o email de redefinição de senha.',
      );
    } finally {
      setResetting(false);
    }
  }, [onSendReset]);

  return (
    <ModalCard>
      <ModalTitle>Editar aluno</ModalTitle>
      <ModalDescription>
        Atualize o nome, email, perfil ou reenvie o link de redefinição de senha do aluno.
      </ModalDescription>
      <Form onSubmit={handleSubmit}>
        <InputLabel>
          Nome
          <TextInput
            placeholder="Nome do aluno"
            value={name}
            onChange={event => setName(event.target.value)}
          />
        </InputLabel>
        <InputLabel>
          Email
          <TextInput
            type="email"
            placeholder="email@exemplo.com"
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
        </InputLabel>
        <InputLabel>
          Perfil
          <Select value={role} onChange={event => setRole(event.target.value as RoleValue)}>
            {ROLE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </InputLabel>
        <HelperText>
          Último acesso: {formatDateTime(student.lastAccessAt ?? student.createdAt)}
        </HelperText>
        {feedback ? <SuccessText>{feedback}</SuccessText> : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
        <ModalActions>
          <ResetPasswordButton type="button" onClick={handleReset} disabled={resetting}>
            {resetting ? 'Enviando...' : 'Enviar redefinição'}
          </ResetPasswordButton>
          <SecondaryButton type="button" onClick={onCancel}>
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </PrimaryButton>
        </ModalActions>
      </Form>
    </ModalCard>
  );
}

type MaterialModalProps = {
  familyOptions: FamilyOption[];
  onSubmit: (values: MaterialFormData) => Promise<void>;
  onCancel: () => void;
};

function MaterialModal({ familyOptions, onSubmit, onCancel }: MaterialModalProps) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const toggleFamily = useCallback((slug: string) => {
    setSelectedFamilies(prev =>
      prev.includes(slug) ? prev.filter(item => item !== slug) : [...prev, slug],
    );
  }, []);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setFile(nextFile);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedTitle = title.trim();
      const hasFile = !!file;

      if (!trimmedTitle || !hasFile || selectedFamilies.length === 0) {
        setError('Informe título, selecione um arquivo e escolha ao menos uma família.');
        return;
      }

      if (saving) {
        return;
      }

      setError(null);
      setSaving(true);

      try {
        await onSubmit({ title: trimmedTitle, file: file as File, families: selectedFamilies });
      } catch (submitError) {
        const message =
          submitError instanceof Error && submitError.message.trim()
            ? submitError.message
            : 'Não foi possível salvar o material.';
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [file, onSubmit, saving, selectedFamilies, title],
  );

  useEffect(() => {
    if (error && title.trim() && file && selectedFamilies.length > 0) {
      setError(null);
    }
  }, [error, file, saving, selectedFamilies.length, title]);

  return (
    <ModalCard>
      <ModalTitle>Novo material</ModalTitle>
      <ModalDescription>
        Envie um arquivo e vincule o material às famílias que poderão baixá-lo.
      </ModalDescription>
      <Form onSubmit={handleSubmit}>
        <InputLabel>
          Título
          <TextInput
            placeholder="Ex.: Planilha de acompanhamento"
            value={title}
            onChange={event => setTitle(event.target.value)}
            disabled={saving}
          />
        </InputLabel>
        <InputLabel>
          Arquivo
          <FileInput
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            onChange={handleFileChange}
            disabled={saving}
          />
          <HelperText>
            {file ? `Selecionado: ${file.name}` : 'Envie PDFs, planilhas ou apresentações para download.'}
          </HelperText>
        </InputLabel>
        <InputLabel>
          Famílias
          <CheckboxGroup>
            {familyOptions.map(option => (
              <CheckboxOption key={option.slug}>
                <input
                  type="checkbox"
                  checked={selectedFamilies.includes(option.slug)}
                  onChange={() => toggleFamily(option.slug)}
                  disabled={saving}
                />
                {option.label}
              </CheckboxOption>
            ))}
          </CheckboxGroup>
        </InputLabel>
        {error ? <ErrorText>{error}</ErrorText> : null}
        <ModalActions>
          <SecondaryButton type="button" onClick={onCancel} disabled={saving}>
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar material'}
          </PrimaryButton>
        </ModalActions>
      </Form>
    </ModalCard>
  );
}

type VideoModalProps = {
  familyOptions: FamilyOption[];
  onSubmit: (values: VideoFormData) => Promise<void>;
  onCancel: () => void;
};

function VideoModal({ familyOptions, onSubmit, onCancel }: VideoModalProps) {
  const [title, setTitle] = useState('');
  const [embedHtml, setEmbedHtml] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const toggleFamily = useCallback((slug: string) => {
    setSelectedFamilies(prev =>
      prev.includes(slug) ? prev.filter(item => item !== slug) : [...prev, slug],
    );
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedTitle = title.trim();
      const trimmedEmbed = embedHtml.trim();

      if (!trimmedTitle || !trimmedEmbed || selectedFamilies.length === 0) {
        setError('Informe título, código embed ou link e pelo menos uma família.');
        return;
      }

      const normalizedEmbed = normalizeVideoEmbedInput(trimmedEmbed);

      if (!normalizedEmbed.success) {
        setError(normalizedEmbed.error);
        return;
      }

      if (saving) {
        return;
      }

      setError(null);
      setSaving(true);

      try {
        await onSubmit({
          title: trimmedTitle,
          embedHtml: normalizedEmbed.embedHtml,
          description: description.trim(),
          families: selectedFamilies,
        });
      } catch (submitError) {
        const message =
          submitError instanceof Error && submitError.message.trim()
            ? submitError.message
            : 'Não foi possível salvar o vídeo.';
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [description, embedHtml, onSubmit, saving, selectedFamilies, title],
  );

  useEffect(() => {
    if (!error) {
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedEmbed = embedHtml.trim();

    if (!trimmedTitle || !trimmedEmbed || selectedFamilies.length === 0) {
      return;
    }

    const normalizedEmbed = normalizeVideoEmbedInput(trimmedEmbed);

    if (normalizedEmbed.success) {
      setError(null);
    }
  }, [embedHtml, error, saving, selectedFamilies.length, title]);

  return (
    <ModalCard>
      <ModalTitle>Nova aula / vídeo</ModalTitle>
      <ModalDescription>
        Cole o embed do Vimeo ou o link do YouTube, escreva uma descrição e indique para quais
        famílias o conteúdo é destinado.
      </ModalDescription>
      <Form onSubmit={handleSubmit}>
        <InputLabel>
          Título
          <TextInput
            placeholder="Ex.: Introdução à certificação"
            value={title}
            onChange={event => setTitle(event.target.value)}
            disabled={saving}
          />
        </InputLabel>
        <InputLabel>
          Embed do vídeo
          <TextArea
            placeholder="Cole o iframe do Vimeo ou um link do YouTube"
            value={embedHtml}
            onChange={event => setEmbedHtml(event.target.value)}
            disabled={saving}
          />
          <HelperText>
            Utilize o código gerado pelo Vimeo em Compartilhar &gt; Incorporar ou cole diretamente o
            link do YouTube.
          </HelperText>
        </InputLabel>
        <InputLabel>
          Descrição
          <TextArea
            placeholder="Contextualize o vídeo para os alunos"
            value={description}
            onChange={event => setDescription(event.target.value)}
            disabled={saving}
          />
        </InputLabel>
        <InputLabel>
          Famílias
          <CheckboxGroup>
            {familyOptions.map(option => (
              <CheckboxOption key={option.slug}>
                <input
                  type="checkbox"
                  checked={selectedFamilies.includes(option.slug)}
                  onChange={() => toggleFamily(option.slug)}
                  disabled={saving}
                />
                {option.label}
              </CheckboxOption>
            ))}
          </CheckboxGroup>
        </InputLabel>
        {error ? <ErrorText>{error}</ErrorText> : null}
        <ModalActions>
          <SecondaryButton type="button" onClick={onCancel} disabled={saving}>
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar vídeo'}
          </PrimaryButton>
        </ModalActions>
      </Form>
    </ModalCard>
  );
}
